import { computed } from 'vue'
import { useAuthStore, type UserPermissions } from '@/stores/auth'

export type ModuleName = keyof UserPermissions
export type ActionName = 'view' | 'create' | 'edit' | 'export' | 'delete' | 'admin' | 'archive' | 'unarchive'

// Niveaux de scope
export const SCOPE_LEVELS = {
  NONE: 0,
  PERSONAL: 1,
  CELLULE: 2,
  CELLULE_LEAD: 3,
  GLOBAL: 4,
} as const

export const SCOPE_LABELS: Record<number, string> = {
  [SCOPE_LEVELS.NONE]: 'Aucun',
  [SCOPE_LEVELS.PERSONAL]: 'Perso',
  [SCOPE_LEVELS.CELLULE]: 'Cellule',
  [SCOPE_LEVELS.CELLULE_LEAD]: 'Cell Lead',
  [SCOPE_LEVELS.GLOBAL]: 'Global',
}

/**
 * Composable pour gérer les permissions utilisateur.
 *
 * Usage:
 * ```ts
 * const { can, canView, canCreate, canEdit, canExport, canDelete } = usePermissions()
 *
 * // Vérification générique
 * if (can('create', 'project')) { ... }
 *
 * // Vérifications spécifiques
 * if (canView('project')) { ... }
 * if (canCreate('finance')) { ... }
 * ```
 */
export function usePermissions() {
  const authStore = useAuthStore()

  /**
   * Récupère le niveau de scope pour un module.
   */
  const getScope = (module: ModuleName): number => {
    const roles = authStore.user?.roles
    if (!roles) return SCOPE_LEVELS.NONE

    // Super admin sans simulation = accès global
    if (roles.is_super_admin && !roles.simulation_active) {
      return SCOPE_LEVELS.GLOBAL
    }

    return roles[module] ?? SCOPE_LEVELS.NONE
  }

  /**
   * Vérifie si l'utilisateur peut effectuer une action sur un module.
   * Utilise les permissions pré-calculées côté serveur si disponibles,
   * sinon calcule localement.
   */
  const can = (action: ActionName, module: ModuleName, resourceCelluleId?: number): boolean => {
    const roles = authStore.user?.roles
    if (!roles) return false

    // Super admin bypass (sauf si simulation active)
    if (roles.is_super_admin && !roles.simulation_active) {
      return true
    }

    // Utiliser les permissions pré-calculées si disponibles
    if (roles.permissions?.[module]) {
      const perms = roles.permissions[module]
      switch (action) {
        case 'view': return perms.canView
        case 'create': return perms.canCreate
        case 'export': return perms.canExport
        case 'edit':
        case 'delete':
          // Si pas de resourceCelluleId, utiliser la permission générale
          if (resourceCelluleId === undefined) {
            return action === 'edit' ? perms.canEdit : perms.canDelete
          }
          // Sinon vérifier que la ressource est dans la cellule de l'utilisateur
          const scope = getScope(module)
          if (scope >= SCOPE_LEVELS.GLOBAL) return true
          if (scope < SCOPE_LEVELS.CELLULE_LEAD) return false
          return authStore.user?.cellule_id === resourceCelluleId
      }
    }

    // Calcul local en fallback
    const level = getScope(module)

    // Dev : super user uniquement (pas même Global)
    if (module === 'dev') {
      return false // Seuls les super admins passent (bypass plus haut)
    }

    // Support : niveau Global (4) uniquement
    if (module === 'support') {
      return level >= SCOPE_LEVELS.GLOBAL
    }

    // Niveaux minimum par module (miroir de config/permissions.php)
    const moduleMinLevels: Partial<Record<ModuleName, { view?: number; edit?: number }>> = {
      finance: { view: SCOPE_LEVELS.CELLULE, edit: SCOPE_LEVELS.CELLULE },
    }

    switch (action) {
      case 'view':
        return level >= (moduleMinLevels[module]?.view ?? SCOPE_LEVELS.PERSONAL)

      case 'create':
      case 'export':
        return level >= SCOPE_LEVELS.CELLULE_LEAD

      case 'admin':
      case 'archive':
      case 'unarchive':
        // Admin/Archive/Unarchive nécessitent le niveau GLOBAL (4)
        return level >= SCOPE_LEVELS.GLOBAL

      case 'edit':
      case 'delete': {
        const minEdit = moduleMinLevels[module]?.edit ?? SCOPE_LEVELS.CELLULE_LEAD
        if (level >= SCOPE_LEVELS.GLOBAL) return true
        if (level < minEdit) return false
        // Si pas de resourceCelluleId, permission générale
        if (resourceCelluleId === undefined) return true
        // Vérifier que la ressource est dans la cellule de l'utilisateur
        return authStore.user?.cellule_id === resourceCelluleId
      }

      default:
        return false
    }
  }

  // Helpers pour chaque action
  const canView = (module: ModuleName) => can('view', module)
  const canCreate = (module: ModuleName) => can('create', module)
  const canEdit = (module: ModuleName, resourceCelluleId?: number) => can('edit', module, resourceCelluleId)
  const canExport = (module: ModuleName) => can('export', module)
  const canDelete = (module: ModuleName, resourceCelluleId?: number) => can('delete', module, resourceCelluleId)
  const canAdmin = (module: ModuleName) => can('admin', module)
  const canArchive = (module: ModuleName) => can('archive', module)
  const canUnarchive = (module: ModuleName) => can('unarchive', module)

  // Computed properties pour accès direct
  const permissions = computed(() => authStore.user?.roles?.permissions)
  const isSuperAdmin = computed(() => authStore.user?.roles?.is_super_admin ?? false)
  const isSimulationActive = computed(() => authStore.user?.roles?.simulation_active ?? false)

  return {
    // Méthodes
    can,
    canView,
    canCreate,
    canEdit,
    canExport,
    canDelete,
    canAdmin,
    canArchive,
    canUnarchive,
    getScope,

    // Computed
    permissions,
    isSuperAdmin,
    isSimulationActive,

    // Constantes
    SCOPE_LEVELS,
    SCOPE_LABELS,
  }
}
